import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, Subscription, debounceTime, filter, map, take } from 'rxjs';

import { SummaryService } from '@app/services/summary.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import {
  selectAllGeneralRoles,
  selectAllUsers,
} from '@main-application/administration/store/selectors/administration.selectors';
import {
  InspectionsSummaryGroupType,
  SummaryStatsV2FilterOption,
} from '@main-application/inspections/services/inspection.service';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import { SummaryDataSource, SummaryType } from '@main-application/shared/services/summary-report-settings.service';
import { selectTenantData } from '@main-application/store/selectors/user.selectors';
import { ButtonMenuComponent } from '@shared/components/button/button-menu/button-menu.component';
import { FadeTextComponent } from '@shared/components/ellipsis-one-row/fade-text.component';
import { TicketStatus } from '@shared/enums/ticket-status-type';
import { pluralizeWord } from '@shared/functions/pluralize-word.function';
import { GeneralRole } from '@shared/interfaces/general-role';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { ColorPickerModule } from '@ui-components/components/color-picker/color-picker.module';
import { InputErrorModule } from '@ui-components/components/input-error/input-error.module';
import { DateDropdownModule } from '@ui-components/controls/date-dropdown/date-dropdown.module';
import { InputModule } from '@ui-components/controls/input/input.module';
import { InputAreaModule } from '@ui-components/controls/input-area/input-area.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';
import { UserSelectDropdownModule } from '@ui-components/controls/user-select-dropdown/user-select-dropdown.module';

import { DialogResult } from '../config/dialog-result.enum';

enum BaseType {
  Report = 1,
  All = 2,
}

@UntilDestroy()
@Component({
  selector: 'app-summary-filter-by-modal',
  standalone: true,
  templateUrl: './summary-filter-by-modal.component.html',
  styleUrls: ['./summary-filter-by-modal.component.scss'],
  imports: [
    NgIf,
    NgFor,
    ReactiveFormsModule,
    MatMenuModule,
    MatButtonModule,
    MatDialogModule,
    MatCheckboxModule,
    RadioButtonDropdownModule,
    UserSelectDropdownModule,
    InputModule,
    InputAreaModule,
    InputErrorModule,
    DateDropdownModule,
    ColorPickerModule,
    MatProgressSpinnerModule,
    ButtonMenuComponent,
    FadeTextComponent,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SummaryFilterByModalComponent extends PuSubscribable implements OnInit {
  BaseType = BaseType;

  public data = inject(MAT_DIALOG_DATA).data;
  private store = inject(Store);
  private summaryService = inject(SummaryService);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private timezoneService = inject(TimezoneService);
  private dialogRef = inject(MatDialogRef<SummaryFilterByModalComponent>);

  public isLoading = true;
  public isSaving = false;

  public filterByOptions: IRadioButtonOption<number | string>[] = [];
  public filteredFilterByOptions: IRadioButtonOption<number | string>[] = [];
  private reportOptions: IRadioButtonOption<number | string>[] = [];
  private allOptions: IRadioButtonOption<number | string>[] = [];
  private pmsNames = 'PMS';

  public baseOptions = [
    { label: 'Search within report’s data', id: BaseType.Report },
    { label: 'Search across all ', id: BaseType.All },
  ];

  filterName = '';

  private readonly today = this.timezoneService.getCurrentDateOnly();
  private filterValuesSub?: Subscription;

  form = this.fb.group({
    id: [null],
    search: [null as string, [Validators.maxLength(255)]],
    filterBy: [null],
    searchBase: [BaseType.All],
    filterValues: this.fb.group({}),
  });

  users$: Observable<RestUserModel[]> = inject(Store).select(selectAllUsers);
  roles$: Observable<IRadioButtonOption<number>[]> = this.store.select(selectAllGeneralRoles).pipe(
    untilDestroyed(this),
    filter((generalRoles: GeneralRole[]) => Array.isArray(generalRoles) && generalRoles?.length > 0),
    map((generalRoles: GeneralRole[]) =>
      generalRoles.map(role => ({
        value: role.id,
        label: role.roleName,
      }))
    )
  );

  ngOnInit(): void {
    if (this.data) {
      this.filterName = this.getFieldTypeName(this.data.filterBy?.fieldType);
      this.baseOptions.find(option => option.id === BaseType.All).label += this.filterName?.toLowerCase() + ' values';

      if (this.form.value.searchBase === BaseType.All) {
        this.onSearchAcrossAll();
      } else {
        this.onSearchReportData();
      }

      this.form.patchValue({
        filterBy: this.data?.filterBy?.fieldType,
      });
    }

    this.store
      .select(selectTenantData)
      .pipe(
        untilDestroyed(this),
        filter(tenantData => !!tenantData),
        take(1)
      )
      .subscribe(tenantData => {
        this.pmsNames = tenantData?.pmses && tenantData?.pmses != '' ? tenantData?.pmses : 'PMS';
      })
      .untilDestroyed(this);

    this.form
      .get('search')!
      .valueChanges.pipe(debounceTime(300))
      .subscribe(() => this.onSearch())
      .untilDestroyed(this);

    this.dialogRef
      .backdropClick()
      .subscribe(() => this.save())
      .untilDestroyed(this);

    this.dialogRef
      .keydownEvents()
      .pipe(filter(event => event.key === 'Escape'))
      .subscribe(() => this.save())
      .untilDestroyed(this);
  }

  cancel() {
    this.dialogRef.close({ result: DialogResult.Fail });
  }

  save() {
    if (!this.form) return;

    this.isSaving = true;

    const filterValuesGroup = this.form.get('filterValues') as FormGroup;
    const selectedValues = Object.keys(filterValuesGroup.value).filter(key => filterValuesGroup.value[key]);

    const selectedOptions = this.filterByOptions.filter(option => selectedValues.includes(String(option.value)));

    const isAllSelected = selectedOptions.length === this.filterByOptions.length || selectedOptions.length === 0;

    const values = isAllSelected ? undefined : selectedOptions.map(opt => opt.value.toString()).join(',');
    const labels = isAllSelected ? undefined : selectedOptions.map(opt => opt.label).join(',');

    const setting = {
      ...this.data.filterBy,
      values,
      labels,
    };

    this.dialogRef.close({ result: DialogResult.Success, setting });
  }

  getDataSourceType(dataSourceType: SummaryDataSource): string {
    switch (dataSourceType) {
      case SummaryDataSource.BB_All:
        return 'Boards (all)';
      case SummaryDataSource.BB_ById:
        return 'Boards (by id)';
      case SummaryDataSource.BB_ByType:
        return 'Boards (by type)';
      case SummaryDataSource.Inspection_Standard_All:
        return 'Inspections (all)';
      case SummaryDataSource.Inspection_Resident_All:
        return 'Resident Inspections (all)';
      case SummaryDataSource.Inspection_Recurring:
        return 'Inspections (repeating)';
      case SummaryDataSource.Service_All:
        return 'Service (all)';
      case SummaryDataSource.Service_OpenTickets:
        return 'Service (open tickets)';
      case SummaryDataSource.Service_CompletedByDate:
        return 'Service (completed by date)';
      case SummaryDataSource.Service_ServiceTickets:
        return 'Service (service tickets)';
      case SummaryDataSource.Turns_All:
        return 'Turns (all)';
      case SummaryDataSource.Turns_ById:
        return 'Turns (by id)';
      case SummaryDataSource.Turns_ByType:
        return 'Turns (by type)';
      case SummaryDataSource.BoardsUsage_All:
        return 'Board Usage (all)';
      case SummaryDataSource.BoardsUsage_ByID:
        return 'Board Usage (by id)';
      default:
        return 'Unknown';
    }
  }

  onSearch(): void {
    const searchValue: string = this.form.get('search')?.value?.toLowerCase() ?? '';

    if (
      this.form.get('searchBase')?.value === BaseType.All &&
      this.form.value.search !== this.lastSearchAcrossAllString
    ) {
      this.onSearchAcrossAll();
    }

    if (this.form.get('searchBase')?.value === BaseType.Report) {
      this.onSearchReportData();
    }

    this.form.get('searchBase')?.value === BaseType.All
      ? this.allOptions.filter(opt => opt.label?.toLowerCase().includes(searchValue))
      : this.reportOptions.filter(opt => opt.label?.toLowerCase().includes(searchValue));

    this.cdr.markForCheck();
  }

  onSearchBaseAction(optionId: number): void {
    this.form.get('searchBase')?.setValue(optionId);

    if (this.form.get('searchBase').value === BaseType.Report) {
      this.onSearchReportData();
    } else {
      this.onSearchAcrossAll();
    }

    this.cdr.markForCheck();
  }

  toggleSearchBase(): void {
    const current = this.form.get('searchBase')?.value;
    const next = current === BaseType.Report ? BaseType.All : BaseType.Report;
    this.form.get('searchBase')?.setValue(next);

    if (this.form.get('searchBase').value === BaseType.Report) {
      this.onSearchReportData();
    } else {
      this.onSearchAcrossAll();
    }

    this.cdr.markForCheck();
  }

  lastSearchAcrossAllString: string;

  onSearchAcrossAll(): void {
    this.isLoading = true;

    if (this.allOptions?.length === 0 || this.form.value.search !== this.lastSearchAcrossAllString) {
      this.summaryService
        .getSummaryStatsFilters(
          this.data.summaryType,
          this.data.summaryReportModifier,
          this.data.filterBy?.fieldType,
          this.form.value.search
        )
        .pipe(take(1))
        .subscribe((data: SummaryStatsV2FilterOption[]) => {
          this.lastSearchAcrossAllString = this.form.value.search;
          this.allOptions = data.map(d => {
            let label =
              (this.data.summaryType === SummaryType.Inspection ||
                this.data.summaryType === SummaryType.InspectionList) &&
              this.data.filterBy?.fieldType === InspectionsSummaryGroupType.Status
                ? String(d.label)
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .trim()
                : String(d.label);

            if (label === 'EventHandler' && this.pmsNames) {
              label = this.pmsNames;
            }

            return {
              label,
              value: d.id,
            };
          });

          this.mergeFilterOptions();
          this.filterBy();
        })
        .untilDestroyed(this);
    } else {
      this.mergeFilterOptions();
      this.filterBy();
    }
  }

  onSearchReportData(): void {
    this.isLoading = true;

    const query = {
      ...this.data.query,
      groupBy: this.data.filterBy?.fieldType,
      subGroupBy: undefined,
    };

    if (this.reportOptions?.length === 0) {
      this.summaryService
        .getSummaryStats(this.data.summaryType, query, [])
        .pipe(take(1))
        .subscribe(data => {
          const mappedOptions = data.map(d => {
            const isInspectionStatus =
              (this.data.summaryType === SummaryType.Inspection ||
                this.data.summaryType === SummaryType.InspectionList) &&
              this.data.filterBy?.fieldType === InspectionsSummaryGroupType.Status;

            let label = String(d.groupName);

            if (isInspectionStatus) {
              const id = Number(d.groupId);
              const statusKey = Object.keys(TicketStatus).find(
                key => TicketStatus[key as keyof typeof TicketStatus] === id
              );

              label =
                !statusKey || id === TicketStatus.NA ? 'N/A' : statusKey.replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
            }

            if (label === 'EventHandler' && this.pmsNames) {
              label = this.pmsNames;
            }

            return {
              label,
              value: d.groupId,
            };
          });

          this.reportOptions = mappedOptions;
          this.mergeFilterOptions();
          this.filterBy();
        })
        .untilDestroyed(this);
    } else {
      this.mergeFilterOptions();
      this.filterBy();
    }
  }

  resetFilters(): void {
    const group: { [key: string]: FormControl } = {};
    this.filterByOptions.forEach(option => {
      const key = String(option.value);
      group[key] = this.fb.control(true);
    });

    this.form.setControl('filterValues', this.fb.group(group));
    this.updateSelectedState();
    this.subscribeToFilterValuesChanges();
    this.cdr.markForCheck();
  }

  public allSelected = false;

  toggleAllOptions(selectAll: boolean): void {
    const filterValuesGroup = this.form.get('filterValues') as FormGroup;
    Object.keys(filterValuesGroup.controls).forEach(key => {
      filterValuesGroup.get([key])?.setValue(selectAll);
    });
    this.cdr.markForCheck();
  }

  filterBy(): void {
    let selectedValues: string[] = [];
    const currentValues = this.form.get('filterValues')?.value;

    if (typeof currentValues === 'object' && Object.keys(currentValues).length > 0) {
      selectedValues = Object.keys(currentValues).filter(key => currentValues[key]);
    } else if (this.data?.filterBy?.values) {
      selectedValues = this.data.filterBy.values.split(',').filter(Boolean);
    } else {
      selectedValues = this.filterByOptions.map(opt => opt.value.toString());
    }

    const formGroup = this.fb.group({});
    this.filterByOptions.forEach(option => {
      const key = String(option.value);
      const isSelected = selectedValues.includes(key);
      formGroup.addControl(key, this.fb.control(isSelected));
    });

    this.form.setControl('filterValues', formGroup);

    const search = this.form.get('search')?.value?.toLowerCase() ?? '';
    const base = this.form.get('searchBase')?.value;

    this.filteredFilterByOptions =
      base === BaseType.Report
        ? this.reportOptions.filter(opt => opt.label?.toLowerCase().includes(search))
        : this.allOptions;

    if (this.allSelected) {
      this.toggleAllOptions(true);
    }

    this.updateSelectedState();
    this.subscribeToFilterValuesChanges();

    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private subscribeToFilterValuesChanges() {
    this.unsubscribeFromFilterValues();
    this.filterValuesSub = this.form
      .get('filterValues')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => this.updateSelectedState())
      .untilDestroyed(this);
  }

  private unsubscribeFromFilterValues() {
    this.filterValuesSub?.unsubscribe();
  }

  getFieldTypeName(type: number): string {
    const match = this.data?.fieldTypes?.find(opt => opt.fieldType === type);
    return match?.label;
  }

  public selectedCount = 0;

  private updateSelectedState(): void {
    const filterValuesGroup = this.form.get('filterValues') as FormGroup;
    if (!filterValuesGroup) return;

    const values = filterValuesGroup.value;
    this.selectedCount = Object.keys(values).filter(key => values[key]).length;
    this.allSelected = this.filterByOptions.every(option => values[String(option.value)] === true);
  }

  private mergeFilterOptions(): void {
    const combined = [...this.filterByOptions, ...this.reportOptions, ...this.allOptions];
    const uniqueMap = new Map<string, IRadioButtonOption<string | number>>();
    combined.forEach(opt => {
      const key = String(opt.value);
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, opt);
      }
    });
    this.filterByOptions = Array.from(uniqueMap.values());
  }

  pluralizeWord(word: string): string {
    return pluralizeWord(word);
  }
}
